import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StagePill from "@/components/StagePill";
import { FileSignature } from "lucide-react";

function Datum({ label, value, sub }) {
  return (
    <div>
      <div className="small-caps text-slate-500">{label}</div>
      <div className="mt-3 font-semibold text-[36px] leading-none text-[#1F2937]">{value}</div>
      {sub && <div className="mt-2 text-[13px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function BorrowerDashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [qual, setQual] = useState(null);
  const [pendingSigs, setPendingSigs] = useState([]);

  useEffect(() => { api.get("/applications").then((r) => setApps(r.data)); }, []);
  useEffect(() => { api.get("/qualifications").then((r) => setQual(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/documents/pending-signatures").then((r) => setPendingSigs(r.data)).catch(() => setPendingSigs([])); }, []);

  const activeApp = apps[0];
  const stageOrder = ["Draft","Submitted","Initial Review","Assigned","Underwriting","Conditional Approval","Approved","Funded"];
  const progress = activeApp ? Math.max(0, (stageOrder.indexOf(activeApp.stage) + 1) * (100 / 8)) : 0;
  const profileComplete = !!(user?.business_name && user?.industry && user?.revenue);

  return (
    <div className="max-w-[1200px] space-y-16" data-testid="borrower-dashboard">
      <header className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="small-caps text-slate-500">My business — {user?.business_name || "Your enterprise"}</div>
          <h1 className="mt-6 font-semibold text-[72px] leading-[0.9] tracking-tight text-[#1F2937]">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"},<br />
            <span className="italic">{user?.name?.split(" ")[0]}.</span>
          </h1>
        </div>
        <Link to="/app/applications/new" className="group inline-flex items-center gap-4" data-testid="start-new-app-btn">
          <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
          <span className="small-caps text-[#1F2937]">Apply For Financing</span>
        </Link>
      </header>

      {!profileComplete && (
        <div className="hair-y py-8 flex items-center justify-between flex-wrap gap-6" data-testid="profile-nudge">
          <div>
            <div className="small-caps text-[#1F2937]">First step</div>
            <h3 className="mt-2 font-semibold text-[28px] text-[#1F2937]">Complete your business profile</h3>
            <p className="mt-2 text-[14.5px] text-slate-600 max-w-lg">
              Add your business name, industry, revenue, and address once — we'll
              pull them into every application you file.
            </p>
          </div>
          <Link to="/app/settings" className="group inline-flex items-center gap-4" data-testid="complete-profile-btn">
            <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
            <span className="small-caps text-[#1F2937]">Complete Profile →</span>
          </Link>
        </div>
      )}

      {pendingSigs.length > 0 && (
        <section
          className="border border-slate-800 bg-white p-6"
          data-testid="pending-signatures-widget"
        >
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <FileSignature className="h-4 w-4 text-[#1F2937]" />
              <div>
                <div className="small-caps text-[#1F2937]">Signature required</div>
                <h3 className="mt-1 font-semibold text-[22px] text-[#1F2937]">
                  {pendingSigs.length} document{pendingSigs.length > 1 ? "s" : ""} awaiting your signature
                </h3>
              </div>
            </div>
          </div>
          <ul className="mt-6 divide-y divide-slate-200">
            {pendingSigs.slice(0, 5).map((d) => (
              <li
                key={d.id}
                className="py-3 flex items-center justify-between gap-4"
                data-testid={`pending-sig-${d.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-[15px] text-[#1F2937] truncate">{d.filename}</div>
                  <div className="small-caps text-slate-500 mt-0.5 font-mono text-[10px]">
                    {d.loan_number} · {d.product_name}
                  </div>
                </div>
                <Link
                  to={`/app/applications/${d.application_id}`}
                  className="small-caps text-[#1F2937] underline underline-offset-4 shrink-0"
                  data-testid={`pending-sig-open-${d.id}`}
                >
                  Review & sign →
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {qual && (
        <section className="rounded-lg border border-slate-200 bg-white p-6 flex items-center justify-between gap-6 flex-wrap" data-testid="qualify-widget">
          <div>
            <div className="text-xs font-medium text-blue-600 uppercase tracking-wide">Auto-qualification</div>
            <h3 className="mt-1 text-xl font-semibold text-slate-900">
              You're ready to apply for {qual.programs.filter(p => p.qualified).length} of {qual.programs.length} programs
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {qual.uploaded_types.length === 0
                ? "Upload a few business documents to unlock programs you qualify for."
                : `${qual.programs.filter(p => !p.qualified && p.percent >= 40).length} more program(s) are close — just need a few more documents.`}
            </p>
          </div>
          <Link to="/app/qualify" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-md text-sm font-medium" data-testid="qualify-widget-cta">
            See what you qualify for →
          </Link>
        </section>
      )}

      <section className="hair-y py-10 grid grid-cols-2 lg:grid-cols-4 gap-10">
        <Datum label="Position" value={activeApp?.stage || "—"} sub={activeApp?.loan_number || "No active file"} />
        <Datum label="Loan Officer" value={activeApp?.loan_rep?.name?.split(" ")[0] || "Unassigned"} sub={activeApp?.loan_rep?.name || ""} />
        <Datum label="Documents" value={activeApp?.required_documents?.length || 0} sub="Required for this file" />
      </section>

      <section className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-8">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="small-caps text-slate-500">Active File</div>
              <h2 className="mt-3 font-semibold text-[40px] leading-tight text-[#1F2937]">{activeApp?.product_name || "No active file"}</h2>
              {activeApp && <p className="mt-2 text-[15px] text-slate-600 max-w-lg">{activeApp.purpose}</p>}
            </div>
            {activeApp && <StagePill stage={activeApp.stage} />}
          </div>

          {activeApp ? (
            <>
              <div className="mt-10 hair-y py-8 grid grid-cols-2 gap-10">
                <div>
                  <div className="small-caps text-slate-500">File</div>
                  <div className="mt-2 font-mono text-[13px] text-[#1F2937]">{activeApp.loan_number}</div>
                </div>
                <div>
                  <div className="small-caps text-slate-500">Amount Requested</div>
                  <div className="mt-2 font-semibold text-[28px] text-[#1F2937]">{fmtMoney(activeApp.requested_amount)}</div>
                </div>
              </div>
              <div className="mt-8">
                <div className="flex items-center justify-between small-caps text-slate-500">
                  <span>Draft → Funded</span>
                  <span className="font-mono">{Math.round(progress)}%</span>
                </div>
                <div className="mt-3 h-[3px] bg-slate-800/10 relative">
                  <div className="absolute inset-y-0 left-0 bg-slate-800" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div className="mt-10 flex items-center gap-8">
                <Link to={`/app/applications/${activeApp.id}`} className="small-caps text-[#1F2937] hover:text-[#1F2937]" data-testid="view-app-detail">View file →</Link>
                <Link to={`/app/documents`} className="small-caps text-slate-500 hover:text-[#1F2937]">Documents →</Link>
              </div>
            </>
          ) : (
            <div className="mt-8 border-t border-slate-200 pt-10">
              <p className="font-semibold text-[22px] text-slate-500 max-w-lg">
                You have no active files under diligence at present.
              </p>
              <Link to="/app/applications/new" className="mt-8 inline-flex items-center gap-4 group" data-testid="empty-start-btn">
                <span className="h-[1px] w-16 bg-slate-800 transition-all group-hover:w-24" />
                <span className="small-caps text-[#1F2937]">Begin an Application</span>
              </Link>
            </div>
          )}
        </div>

        <aside className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l lg:border-slate-200">
          <div className="small-caps text-slate-500">Required Documents</div>
          <ul className="mt-6">
            {(activeApp?.required_documents || []).slice(0, 8).map((d, i) => (
              <li key={d} className="grid grid-cols-12 items-baseline py-3 border-b border-slate-200">
                <span className="col-span-2 font-mono text-[10px] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                <span className="col-span-8 font-semibold text-[15px] text-slate-700">{d}</span>
                <span className="col-span-2 small-caps text-[#1F2937] text-right">Open</span>
              </li>
            ))}
            {(!activeApp || !activeApp.required_documents?.length) && (
              <li className="text-[14px] text-slate-500 italic">A checklist will populate upon file creation.</li>
            )}
          </ul>
        </aside>
      </section>

      <section className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-7">
          <div className="small-caps text-slate-500">Need help?</div>
          <h3 className="mt-3 font-semibold text-[28px] text-[#1F2937]">Contact your loan officer</h3>
          <p className="mt-4 text-[14.5px] leading-[1.7] text-slate-600">
            Your assigned loan officer will reach out to you directly. Watch for a
            text or a phone call — replies come the same day, Mon–Fri 8am–7pm ET.
          </p>
        </div>
      </section>
    </div>
  );
}
